import type {
  ApiAvailableReaction,
  ApiChatReactions,
  ApiMessage,
  ApiPeerReaction,
  ApiReactionCount,
  ApiReactionKey,
  ApiReactions,
  ApiReactionWithPaid,
} from '../../api/types';
import type { MessageKey } from '../../util/keys/messageKey';
import type { GlobalState } from '../types';

export function getMessageRecentReaction(message: Partial<ApiMessage>) {
  return message.isOutgoing ? message.reactions?.recentReactions?.[0] : undefined;
}

// `toggleReaction` fires a `sendReaction` request per click with no cancellation, so rapid toggles
// on the same message can have their responses resolve out of order. Track the latest request per
// message so a late-arriving stale response can be told apart from the current one and dropped
// instead of clobbering fresher state.
const latestReactionRequestByMessageKey = new Map<string, number>();
let reactionRequestCounter = 0;

export function registerReactionRequest(messageKey: string): number {
  const requestId = ++reactionRequestCounter;
  latestReactionRequestByMessageKey.set(messageKey, requestId);
  return requestId;
}

export function isLatestReactionRequest(messageKey: string, requestId: number): boolean {
  return latestReactionRequestByMessageKey.get(messageKey) === requestId;
}
export function checkIfHasUnreadReactions(global: GlobalState, reactions: ApiReactions) {
  const { currentUserId } = global;
  return reactions?.recentReactions?.some(
    ({ isUnread, isOwn, peerId }) => isUnread && !isOwn && currentUserId !== peerId,
  );
}

// The backend keeps no per-message reaction read state: `/messaging/unread-reactions` answers with
// every message that carries a reaction and marking them read is an ack that changes nothing. So the
// viewport observer keeps handing `animateUnreadReaction` the whole set, and every message holding
// that reaction replays its effect at once. Remember the reaction events already played and let each
// of them animate a single time.
const playedReactionEffectKeys = new Set<string>();
const PLAYED_REACTION_EFFECTS_LIMIT = 500;

// Past this age a reaction is not news but something the user has already lived with, so it never
// earns an effect — this is what keeps a reload from replaying every old reaction in the chat, since
// the set above starts empty. A reaction with no `addedDate` is treated as fresh: the flag is the
// backend's to send, and guessing "old" there would silence the effect altogether.
const REACTION_EFFECT_MAX_AGE_SECONDS = 5 * 60;

export function checkIfReactionIsUnreadByOther(global: GlobalState, reaction?: ApiPeerReaction) {
  if (!reaction?.isUnread || reaction.isOwn) return false;
  // The backend omits `isOwn`, so the reaction the user just sent comes back looking like someone
  // else's — compare the peer too, the same way `checkIfHasUnreadReactions` does.
  return reaction.peerId !== global.currentUserId;
}

export function checkIfReactionEffectIsStale(addedDate?: number) {
  if (!addedDate) return false;
  return Date.now() / 1000 - addedDate > REACTION_EFFECT_MAX_AGE_SECONDS;
}

// Keyed by the reaction event rather than the message, so the same peer reacting anew still animates
// while the backend re-serving the very same reaction does not.
export function checkIfReactionEffectPlayed(messageKey: MessageKey, reaction: ApiPeerReaction) {
  return playedReactionEffectKeys.has(buildReactionEffectKey(messageKey, reaction));
}

export function markReactionEffectPlayed(messageKey: MessageKey, reaction: ApiPeerReaction) {
  playedReactionEffectKeys.add(buildReactionEffectKey(messageKey, reaction));

  while (playedReactionEffectKeys.size > PLAYED_REACTION_EFFECTS_LIMIT) {
    const oldestKey: string = playedReactionEffectKeys.values().next().value!;
    playedReactionEffectKeys.delete(oldestKey);
  }
}

function buildReactionEffectKey(messageKey: MessageKey, reaction: ApiPeerReaction) {
  return [messageKey, getReactionKey(reaction.reaction), reaction.peerId, reaction.addedDate].join('_');
}

export function areReactionsEmpty(reactions: ApiReactions) {
  return !reactions.results.some(({ count, localAmount }) => count || localAmount);
}

// The reaction catalog stores bare codepoints — `❤` is U+2764, never U+2764 U+FE0F — and reactions
// are matched to their catalog icon by exact emoticon string. A client that sends the presentation
// variant (`setMessageReaction` with `❤️` is the easy mistake) therefore stores fine but misses the
// lookup, leaving `ReactionStaticEmoji` with no `staticIcon` and painting a blank 1×1 pill. Fold the
// variation selectors away so both spellings key the same reaction.
// A keycap (`1️⃣` = `1` U+FE0F U+20E3) needs its U+FE0F to stay, so that one sequence is excluded.
export function normalizeEmoticon(emoticon: string) {
  return emoticon.replace(/\uFE0F(?!\u20E3)/g, '').replace(/\uFE0E/g, '');
}

export function getReactionKey(reaction: ApiReactionWithPaid): ApiReactionKey {
  switch (reaction.type) {
    case 'emoji':
      return `emoji-${normalizeEmoticon(reaction.emoticon)}`;
    case 'custom':
      return `document-${reaction.documentId}`;
    case 'paid':
      return 'paid';
    default: {
      // Legacy reactions
      const uniqueValue = (reaction as any).emoticon || (reaction as any).documentId;
      return `unsupported-${uniqueValue}`;
    }
  }
}

export function isSameReaction(first?: ApiReactionWithPaid, second?: ApiReactionWithPaid) {
  if (first === second) {
    return true;
  }

  if (!first || !second) {
    return false;
  }

  return getReactionKey(first) === getReactionKey(second);
}

export function canSendReaction(reaction: ApiReactionWithPaid, chatReactions: ApiChatReactions) {
  if (chatReactions.type === 'all') {
    return reaction.type === 'emoji' || chatReactions.areCustomAllowed;
  }

  if (chatReactions.type === 'some') {
    return chatReactions.allowed.some((r) => isSameReaction(r, reaction));
  }

  return false;
}

export function sortReactions<T extends ApiAvailableReaction | ApiReactionWithPaid>(
  reactions: T[],
  topReactions?: ApiReactionWithPaid[],
): T[] {
  return reactions.slice().sort((left, right) => {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion -- TS Bug?
    const reactionOne = left ? ('reaction' in left ? left.reaction : left as ApiReactionWithPaid) : undefined;
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion -- TS Bug?
    const reactionTwo = right ? ('reaction' in right ? right.reaction : right as ApiReactionWithPaid) : undefined;

    if (reactionOne?.type === 'paid') return -1;
    if (reactionTwo?.type === 'paid') return 1;

    const indexOne = topReactions?.findIndex((reaction) => isSameReaction(reaction, reactionOne)) || 0;
    const indexTwo = topReactions?.findIndex((reaction) => isSameReaction(reaction, reactionTwo)) || 0;
    return (
      (indexOne > -1 ? indexOne : Infinity) - (indexTwo > -1 ? indexTwo : Infinity)
    );
  });
}

export function getUserReactions(message: ApiMessage): ApiReactionWithPaid[] {
  return message.reactions?.results?.filter((r): r is Required<ApiReactionCount> => isReactionChosen(r))
    .sort((a, b) => a.chosenOrder - b.chosenOrder)
    .map((r) => r.reaction) || [];
}

export function isReactionChosen(reaction: ApiReactionCount) {
  return reaction.chosenOrder !== undefined;
}

export function updateReactionCount(reactionCount: ApiReactionCount[], newReactions: ApiReactionWithPaid[]) {
  const results = reactionCount.map((current) => (
    isReactionChosen(current) ? {
      ...current,
      chosenOrder: undefined,
      count: current.count - 1,
    } : current
  )).filter(({ count }) => count > 0);

  newReactions.forEach((reaction, i) => {
    const existingIndex = results.findIndex((r) => isSameReaction(r.reaction, reaction));
    if (existingIndex > -1) {
      results[existingIndex] = {
        ...results[existingIndex],
        chosenOrder: i,
        count: results[existingIndex].count + 1,
      };
    } else {
      results.push({
        reaction,
        chosenOrder: i,
        count: 1,
      });
    }
  });

  return results;
}

export function addPaidReaction(
  reactionCount: ApiReactionCount[], count: number, isAnonymous?: boolean, peerId?: string,
): ApiReactionCount[] {
  const results: ApiReactionCount[] = [];
  const hasPaid = reactionCount.some((current) => current.reaction.type === 'paid');
  if (hasPaid) {
    reactionCount.forEach((current) => {
      if (current.reaction.type === 'paid') {
        results.push({
          ...current,
          localAmount: (current.localAmount || 0) + count,
          chosenOrder: -1,
          localIsPrivate: isAnonymous !== undefined ? isAnonymous : current.localIsPrivate,
          localPeerId: peerId || current.localPeerId,
          localPreviousChosenOrder: current.chosenOrder,
        });
        return;
      }

      results.push(current);
    });

    return results;
  }

  return [
    {
      reaction: { type: 'paid' },
      count: 0,
      chosenOrder: -1,
      localAmount: count,
      localIsPrivate: isAnonymous,
      localPeerId: peerId,
    },
    ...reactionCount,
  ];
}
